import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Bold, Italic, Underline, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote,
  AlignLeft, AlignCenter, AlignRight, Link2, Unlink, ImagePlus, ImageUp, Code2, Undo2, Redo2,
  Minus, Palette, Highlighter, Eraser, Loader2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const PALETTE = ["#0F172A", "#475569", "#1E40AF", "#0284C7", "#DC2626", "#D97706", "#059669", "#7C3AED", "#DB2777", "#FFFFFF"];
const HIGHLIGHTS = ["#FEF3C7", "#DBEAFE", "#DCFCE7", "#FEE2E2", "#F3E8FF", "#F1F5F9"];

const Btn = ({ onClick, active, title, children, testId, disabled }) => (
  <button type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled} data-testid={testId}
    className={`inline-flex h-8 w-8 items-center justify-center rounded transition-colors duration-150 disabled:opacity-40 ${active ? "bg-blue-100 text-blue-800" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
    {children}
  </button>
);

const ColorPicker = ({ title, icon: Icon, colors, onPick, onClear, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" title={title} aria-label={title} data-testid={testId}
        className="inline-flex h-8 w-8 items-center justify-center rounded text-slate-600 transition-colors duration-150 hover:bg-slate-100"><Icon className="h-4 w-4" /></button>
    </PopoverTrigger>
    <PopoverContent className="w-56 p-3">
      <p className="mb-2 text-xs font-medium text-slate-500">{title}</p>
      <div className="grid grid-cols-5 gap-1.5">
        {colors.map((c) => (
          <button key={c} type="button" onClick={() => onPick(c)} title={c} data-testid={`${testId}-${c.slice(1)}`}
            className="h-7 w-7 rounded border border-slate-200 transition-transform duration-150 hover:scale-110" style={{ background: c }} />
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-slate-600">
          Custom <input type="color" onChange={(e) => onPick(e.target.value)} className="h-7 w-10 cursor-pointer rounded border border-slate-200" data-testid={`${testId}-custom`} />
        </label>
        <button type="button" onClick={onClear} className="text-xs text-slate-500 hover:text-red-600" data-testid={`${testId}-clear`}>Reset</button>
      </div>
    </PopoverContent>
  </Popover>
);

const Sep = () => <span className="mx-1 h-5 w-px bg-slate-200" />;

export const EditorToolbar = ({ editor, source, toggleSource, testId }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const chain = () => editor.chain().focus();
  const off = source;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "https://";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (!url.trim()) { chain().extendMarkRange("link").unsetLink().run(); return; }
    const newTab = window.confirm("Open this link in a new tab?");
    chain().extendMarkRange("link").setLink({ href: url.trim(), target: newTab ? "_blank" : null }).run();
  };

  const imageUrl = () => {
    const url = window.prompt("Image URL", "https://");
    if (url && url.trim() && url !== "https://") chain().setImage({ src: url.trim() }).run();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      chain().setImage({ src: data.url, alt: file.name }).run();
      toast.success("Image added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setUploading(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5" data-testid={`${testId}-toolbar`}>
      <Btn title="Bold" testId={`${testId}-bold`} disabled={off} active={editor.isActive("bold")} onClick={() => chain().toggleBold().run()}><Bold className="h-4 w-4" /></Btn>
      <Btn title="Italic" testId={`${testId}-italic`} disabled={off} active={editor.isActive("italic")} onClick={() => chain().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>
      <Btn title="Underline" testId={`${testId}-underline`} disabled={off} active={editor.isActive("underline")} onClick={() => chain().toggleUnderline().run()}><Underline className="h-4 w-4" /></Btn>
      <Btn title="Strikethrough" testId={`${testId}-strike`} disabled={off} active={editor.isActive("strike")} onClick={() => chain().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></Btn>
      <Sep />
      <Btn title="Heading" testId={`${testId}-h2`} disabled={off} active={editor.isActive("heading", { level: 2 })} onClick={() => chain().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></Btn>
      <Btn title="Subheading" testId={`${testId}-h3`} disabled={off} active={editor.isActive("heading", { level: 3 })} onClick={() => chain().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></Btn>
      <ColorPicker title="Text colour" icon={Palette} colors={PALETTE} testId={`${testId}-color`} onPick={(c) => chain().setColor(c).run()} onClear={() => chain().unsetColor().run()} />
      <ColorPicker title="Highlight colour" icon={Highlighter} colors={HIGHLIGHTS} testId={`${testId}-highlight`} onPick={(c) => chain().setHighlight({ color: c }).run()} onClear={() => chain().unsetHighlight().run()} />
      <Sep />
      <Btn title="Bullet list" testId={`${testId}-ul`} disabled={off} active={editor.isActive("bulletList")} onClick={() => chain().toggleBulletList().run()}><List className="h-4 w-4" /></Btn>
      <Btn title="Numbered list" testId={`${testId}-ol`} disabled={off} active={editor.isActive("orderedList")} onClick={() => chain().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Btn>
      <Btn title="Quote" testId={`${testId}-quote`} disabled={off} active={editor.isActive("blockquote")} onClick={() => chain().toggleBlockquote().run()}><Quote className="h-4 w-4" /></Btn>
      <Btn title="Divider" testId={`${testId}-hr`} disabled={off} onClick={() => chain().setHorizontalRule().run()}><Minus className="h-4 w-4" /></Btn>
      <Sep />
      <Btn title="Align left" testId={`${testId}-align-left`} disabled={off} active={editor.isActive({ textAlign: "left" })} onClick={() => chain().setTextAlign("left").run()}><AlignLeft className="h-4 w-4" /></Btn>
      <Btn title="Align centre" testId={`${testId}-align-center`} disabled={off} active={editor.isActive({ textAlign: "center" })} onClick={() => chain().setTextAlign("center").run()}><AlignCenter className="h-4 w-4" /></Btn>
      <Btn title="Align right" testId={`${testId}-align-right`} disabled={off} active={editor.isActive({ textAlign: "right" })} onClick={() => chain().setTextAlign("right").run()}><AlignRight className="h-4 w-4" /></Btn>
      <Sep />
      <Btn title="Add / edit link" testId={`${testId}-link`} disabled={off} active={editor.isActive("link")} onClick={setLink}><Link2 className="h-4 w-4" /></Btn>
      <Btn title="Remove link" testId={`${testId}-unlink`} disabled={off || !editor.isActive("link")} onClick={() => chain().unsetLink().run()}><Unlink className="h-4 w-4" /></Btn>
      <Btn title="Upload image" testId={`${testId}-image-upload`} disabled={off || uploading} onClick={() => fileRef.current?.click()}>
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageUp className="h-4 w-4" />}
      </Btn>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={upload} data-testid={`${testId}-image-input`} />
      <Btn title="Image from URL" testId={`${testId}-image-url`} disabled={off} onClick={imageUrl}><ImagePlus className="h-4 w-4" /></Btn>
      <Btn title="Clear formatting" testId={`${testId}-clear-format`} disabled={off} onClick={() => chain().unsetAllMarks().clearNodes().run()}><Eraser className="h-4 w-4" /></Btn>
      <Sep />
      <Btn title="Undo" testId={`${testId}-undo`} disabled={off} onClick={() => chain().undo().run()}><Undo2 className="h-4 w-4" /></Btn>
      <Btn title="Redo" testId={`${testId}-redo`} disabled={off} onClick={() => chain().redo().run()}><Redo2 className="h-4 w-4" /></Btn>
      <button type="button" onClick={toggleSource} data-testid={`${testId}-source-toggle`}
        className={`ml-auto inline-flex h-8 items-center gap-1.5 rounded px-2.5 text-xs font-medium transition-colors duration-150 ${source ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
        <Code2 className="h-4 w-4" />{source ? "Visual editor" : "HTML"}
      </button>
    </div>
  );
};
